import type {
  AdminOpsSettings,
  AdminOpsSettingsInput,
} from "@/features/admin/api/admin-ops.api";

/** Fields the page renders. The credit and the Slack flag are read-only. */
export const ADMIN_OPS_SETTING_KEYS = [
  "eventsRetentionDays",
  "eventsHidePingsDefault",
  "eventsDefaultWindowHours",
  "eventsStuckAfterMinutes",
  "alertOnFailedEvents",
  "failedEventsAlertThreshold",
  "alertOnSlowActions",
  "slowActionSecondsDefault",
  "slowActionSecondsByEventType",
  "eventsChartLatencyMetric",
  "eventsChartShowBudgetLine",
  "jobsOverdueGraceMinutes",
  "jobsUnhealthyAfterFailures",
  "jobsAutoRefreshSeconds",
  "alertOnJobFailures",
  "alertOnJobMissed",
  "alertOnJobLifecycle",
  "tokenUsageRetentionDays",
  "tokenUsageDefaultWindowHours",
  "tokenUsageAutoRefreshSeconds",
  "tokenUsageDefaultGrouping",
  "tokenUsageLongTailPercent",
  "alertOnTokenSpend",
  "tokenSpendAlertThreshold",
  "tokenSpendAlertWindowHours",
  "tokenSpendAlertCooldownHours",
  "alertOnTenantTokenSpend",
  "tenantTokenSpendAlertThreshold",
  "alertOnLlmLatency",
  "llmLatencyAlertP95Ms",
  "monthlyBudgetUsdCents",
  "budgetAlertAtPercent",
  "bigQueryPricePerTbMicros",
  "bigQueryRetentionDays",
  "alertOnBigQueryBytes",
  "bigQueryBytesAlertThreshold",
  "alertOnBigQuerySingleQueryBytes",
  "bigQuerySingleQueryBytesThreshold",
  "bigQueryBytesAlertWindowHours",
  "bigQueryBytesAlertCooldownHours",
] as const satisfies ReadonlyArray<keyof AdminOpsSettingsInput>;

export type AdminOpsSettingKey = (typeof ADMIN_OPS_SETTING_KEYS)[number];

/** The editable half of a settings record, which is what the form holds. */
export type AdminOpsSettingsDraft = Pick<AdminOpsSettings, AdminOpsSettingKey>;

/**
 * What the Events, Scheduled jobs, and Token usage tabs assume before the
 * settings have loaded — or when the viewer has no grant to read them. Mirrors
 * `ADMIN_OPS_SETTINGS_DEFAULTS` in @nexus/db so an admin without the grant
 * sees the same tab a fresh install shows.
 */
export const ADMIN_OPS_FALLBACK: AdminOpsSettingsDraft = {
  eventsRetentionDays: 0,
  eventsHidePingsDefault: true,
  eventsDefaultWindowHours: 24,
  eventsStuckAfterMinutes: 180,
  alertOnFailedEvents: false,
  failedEventsAlertThreshold: 5,
  alertOnSlowActions: false,
  slowActionSecondsDefault: 600,
  slowActionSecondsByEventType: {},
  eventsChartLatencyMetric: "both",
  eventsChartShowBudgetLine: true,
  jobsOverdueGraceMinutes: 5,
  jobsUnhealthyAfterFailures: 3,
  jobsAutoRefreshSeconds: 0,
  alertOnJobFailures: true,
  alertOnJobMissed: true,
  alertOnJobLifecycle: false,
  tokenUsageRetentionDays: 0,
  tokenUsageDefaultWindowHours: 24,
  tokenUsageAutoRefreshSeconds: 0,
  tokenUsageDefaultGrouping: "model",
  tokenUsageLongTailPercent: 1,
  alertOnTokenSpend: false,
  tokenSpendAlertThreshold: 1_000_000,
  tokenSpendAlertWindowHours: 1,
  tokenSpendAlertCooldownHours: 6,
  alertOnTenantTokenSpend: false,
  tenantTokenSpendAlertThreshold: 500_000,
  alertOnLlmLatency: false,
  llmLatencyAlertP95Ms: 30_000,
  monthlyBudgetUsdCents: 0,
  budgetAlertAtPercent: 80,
  bigQueryPricePerTbMicros: 0,
  bigQueryRetentionDays: 90,
  alertOnBigQueryBytes: false,
  bigQueryBytesAlertThreshold: 10_995_116_277_760,
  alertOnBigQuerySingleQueryBytes: false,
  bigQuerySingleQueryBytesThreshold: 1_099_511_627_776,
  bigQueryBytesAlertWindowHours: 24,
  bigQueryBytesAlertCooldownHours: 6,
};

export function toOpsDraft(settings: AdminOpsSettings): AdminOpsSettingsDraft {
  return ADMIN_OPS_SETTING_KEYS.reduce((draft, key) => {
    (draft as Record<string, unknown>)[key] = settings[key];
    return draft;
  }, {} as AdminOpsSettingsDraft);
}

/**
 * The fields that differ from what the server last returned, so a save never
 * writes back a field somebody else changed meanwhile.
 */
export function changedOpsSettings(
  draft: AdminOpsSettingsDraft,
  saved: AdminOpsSettings,
): Partial<AdminOpsSettingsInput> {
  const changes: Record<string, unknown> = {};
  for (const key of ADMIN_OPS_SETTING_KEYS) {
    if (!isSameSetting(draft[key], saved[key])) changes[key] = draft[key];
  }
  return changes as Partial<AdminOpsSettingsInput>;
}

/**
 * The per-event-type budgets are an object, and a refetch hands back a new one
 * every time. Comparing by reference would leave the page permanently claiming
 * an unsaved change, so objects are compared by their sorted entries.
 */
function isSameSetting(draft: unknown, saved: unknown): boolean {
  if (typeof draft !== "object" || draft === null || typeof saved !== "object" || saved === null) {
    return draft === saved;
  }
  return JSON.stringify(sortedEntries(draft)) === JSON.stringify(sortedEntries(saved));
}

function sortedEntries(value: object): Array<[string, unknown]> {
  return Object.entries(value).sort(([a], [b]) => a.localeCompare(b));
}
